import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react';
import { getBillingInformation, updateBillingInformation } from '../..';
import type { ICardInformation, IUpgradeFormOnSubmit } from '../../interfaces';

interface BillingContextValue {
  billingInfo: ICardInformation | null;
  isSubmitting: boolean;
  handleSubmit: (data: IUpgradeFormOnSubmit) => Promise<void>;
}

interface BillingProviderProps {
  onSuccess?: (response: unknown) => void;
}

const BillingContext = createContext<BillingContextValue | null>(null);

export const useBillingInfo = () => {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};

export const BillingProvider = ({
  children,
  onSuccess,
}: PropsWithChildren<BillingProviderProps>) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [billingInfo, setBillingInfo] = useState<ICardInformation | null>(null);

  useEffect(() => {
    getBillingInformation().then((res) => setBillingInfo(res as ICardInformation));
  }, []);

  const handleSubmit = async ({ id, type }: IUpgradeFormOnSubmit) => {
    setIsSubmitting(true);
    try {
      const response = await updateBillingInformation({
        token_id: id,
        type,
      });
      onSuccess?.(response);
    } finally {
      setIsSubmitting(false);
    }
  };

  const value = {
    billingInfo,
    isSubmitting,
    handleSubmit,
  };

  return <BillingContext.Provider value={value}>{children}</BillingContext.Provider>;
};
